import { useEffect, useState } from 'react';
import { Send, Users, FileText, AlertCircle, MessageSquare, Megaphone } from 'lucide-react';
import { listCompanies, sendBroadcast, Company } from '../services/api';

const MSG_TYPES = [
  { value: 'announcement', label: 'お知らせ',   bg: '#eff6ff', color: '#1d4ed8', Icon: Megaphone },
  { value: 'general',      label: '一般',        bg: '#f8fafc', color: '#475569', Icon: MessageSquare },
  { value: 'emergency',    label: '緊急',        bg: '#fff1f2', color: '#be123c', Icon: AlertCircle },
];

export default function BroadcastPage() {
  const [companies, setCompanies]   = useState<Company[]>([]);
  const [loading, setLoading]       = useState(true);
  const [recipientMode, setRecipientMode] = useState<'all' | 'select'>('all');
  const [selected, setSelected]     = useState<Set<string>>(new Set());
  const [content, setContent]       = useState('');
  const [msgType, setMsgType]       = useState('announcement');
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState('');
  const [result, setResult]         = useState<{ sent_to: number } | null>(null);

  useEffect(() => {
    listCompanies()
      .then(list => { setCompanies(list.filter(c => c.is_active)); })
      .catch(e => setError(e instanceof Error ? e.message : 'エラーが発生しました'))
      .finally(() => setLoading(false));
  }, []);

  function toggleCompany(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === companies.length) setSelected(new Set());
    else setSelected(new Set(companies.map(c => c.id)));
  }

  const recipientCount = recipientMode === 'all' ? companies.length : selected.size;
  const canSend = !sending && content.trim().length > 0 && (recipientMode === 'all' || selected.size > 0);

  async function handleSend() {
    if (!content.trim()) { setError('メッセージ内容を入力してください。'); return; }
    if (recipientMode === 'select' && selected.size === 0) { setError('送信先を選択してください。'); return; }
    if (!window.confirm(`${recipientCount}社にメッセージを送信します。よろしいですか？`)) return;
    setSending(true); setError('');
    try {
      const ids = recipientMode === 'all' ? 'all' : Array.from(selected);
      const res = await sendBroadcast(ids, content.trim(), msgType);
      setResult(res);
      setContent('');
      setSelected(new Set());
    } catch (e) { setError(e instanceof Error ? e.message : 'エラーが発生しました'); }
    finally { setSending(false); }
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.3px' }}>一括メッセージ送信</h1>
      </div>

      {result && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 36, height: 36, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#15803d', fontSize: 14 }}>{result.sent_to}社にメッセージを送信しました</div>
            <button onClick={() => setResult(null)} style={{ background: 'none', border: 'none', color: '#16a34a', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0, marginTop: 4 }}>
              続けて送信する
            </button>
          </div>
        </div>
      )}

      {!result && (
        <div style={{ display: 'grid', gap: 16, maxWidth: 800 }}>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '11px 16px', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {/* 送信先 */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
              <Users size={14} color="#6b7280" />
              <span style={sectionTitle}>送信先</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[{ v: 'all', label: `全会社（${companies.length}社）` }, { v: 'select', label: '会社を選択' }].map(opt => (
                <button key={opt.v} onClick={() => setRecipientMode(opt.v as 'all' | 'select')} style={{
                  background: recipientMode === opt.v ? '#1e40af' : '#fff',
                  color: recipientMode === opt.v ? '#fff' : '#6b7280',
                  border: `1px solid ${recipientMode === opt.v ? '#1e40af' : '#d1d5db'}`,
                  borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}>
                  {opt.label}
                </button>
              ))}
            </div>

            {recipientMode === 'select' && (
              loading ? (
                <div style={{ color: '#94a3b8', fontSize: 13 }}>読み込み中...</div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 500 }}>
                      <input type="checkbox" checked={selected.size === companies.length && companies.length > 0} onChange={toggleAll} />
                      全選択 / 解除
                    </label>
                    {selected.size > 0 && (
                      <span style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600, background: '#eff6ff', padding: '2px 8px', borderRadius: 99 }}>{selected.size}社 選択中</span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 7, maxHeight: 260, overflowY: 'auto' }}>
                    {companies.map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: `1px solid ${selected.has(c.id) ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 7, cursor: 'pointer', background: selected.has(c.id) ? '#eff6ff' : '#fafafa', fontSize: 13 }}>
                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleCompany(c.id)} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selected.has(c.id) ? 600 : 400, color: '#374151' }}>{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>

          {/* メッセージ */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 16 }}>
              <FileText size={14} color="#6b7280" />
              <span style={sectionTitle}>メッセージ</span>
            </div>

            {/* 種別 */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>種別</label>
              <div style={{ display: 'flex', gap: 7 }}>
                {MSG_TYPES.map(t => {
                  const active = msgType === t.value;
                  return (
                    <button key={t.value} onClick={() => setMsgType(t.value)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: active ? t.bg : '#f8fafc',
                      color: active ? t.color : '#94a3b8',
                      border: `1px solid ${active ? t.color + '80' : '#e2e8f0'}`,
                      borderRadius: 7, padding: '7px 14px', fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
                    }}>
                      <t.Icon size={13} strokeWidth={active ? 2.5 : 1.8} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 本文 */}
            <div>
              <label style={labelStyle}>本文</label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={5}
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', color: '#0f172a', lineHeight: 1.6 }}
                placeholder="管理者に伝えたい内容を入力してください..."
              />
              <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 4 }}>{content.length} 文字</div>
            </div>

            {/* プレビュー */}
            {content.trim() && (() => {
              const mt = MSG_TYPES.find(t => t.value === msgType)!;
              return (
                <div style={{ background: mt.bg, border: `1px solid ${mt.color}28`, borderRadius: 8, padding: '12px 14px', marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: mt.color, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{mt.label} — プレビュー</div>
                  <div style={{ fontSize: 14, color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{content}</div>
                </div>
              );
            })()}
          </div>

          {/* 送信 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={handleSend}
              disabled={!canSend}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: canSend ? '#1e40af' : '#94a3b8',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '11px 22px', fontSize: 14, fontWeight: 600, cursor: canSend ? 'pointer' : 'not-allowed',
              }}
            >
              <Send size={14} />
              {sending ? '送信中...' : `${recipientCount}社に送信する`}
            </button>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              送信したメッセージは各会社の管理画面に表示されます
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const cardStyle: React.CSSProperties  = { background: '#fff', borderRadius: 10, padding: '20px 22px', border: '1px solid #e5e7eb' };
const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#374151' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, letterSpacing: '0.01em' };
